import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DataTableComponent } from 'src/app/components/data-table/data-table.component';
import { InlineActionComponent } from 'src/app/components/data-table/inline-action/inline-action.component';
import { MaterialModule } from 'src/app/material.module';
import { LoadingService } from 'src/app/services/loading.service';
import { ToastService } from 'src/app/services/toast.service';
import { TableService } from 'src/app/services/table.service';
import { UserService } from 'src/app/services/user.service';
import { ChipComponent } from 'src/app/shared/chip/chip.component';
import { LicenseStatusComponent } from 'src/app/shared/components/license-status/license-status.component';
import { DIALOG_WIDTH, PARAM_OPERATOR, ROUTES } from 'src/app/shared/constants';
import { FilterType, TableDataType, TableLayout, TextFormat } from 'src/app/shared/enums';
import { ISubmission } from 'src/app/shared/interfaces/IForm';
import { IRetrieveParam, ITableConfig, ITopOptions } from 'src/app/shared/interfaces/ITable';
import { UserModalComponent } from './user-modal/user-modal.component';
import { TextFormatterComponent } from 'src/app/components/data-table/text-formatter/text-formatter.component';
import { tap } from 'rxjs';

@Component({
  selector: 'app-user',
  imports: [DataTableComponent, MaterialModule],
  templateUrl: './user.component.html'
})
export class UserComponent implements OnInit {
  @Input() companyId?: number;
  @Input() totalChanged?: (total: number) => void;
  @ViewChild(DataTableComponent) dataTable: DataTableComponent;
  tableConfig: ITableConfig;
  topOptions: ITopOptions = {};
  params: IRetrieveParam = { page: 1, pageSize: 10, sortText: 'lastLogin.desc', globalSearch: '' };
  isAdminSystem = this.router.url.startsWith(ROUTES.ADMIN.USER);

  get isStandaloneUserTab(): boolean {
    return this.isAdminSystem && !this.companyId;
  }

  get tableId(): string {
    return this.companyId ? `company-${this.companyId}-user-table` : 'user-table';
  }

  constructor(
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private router: Router,
    private translateService: TranslateService,
    private loadingService: LoadingService,
    private toastService: ToastService,
    private tableService: TableService,
    private userService: UserService) {}

  ngOnInit() {
    this.companyId = this.companyId ?? this.route.snapshot.pathFromRoot.find(r => r.params['companyId'])?.params['companyId'];
    this.initTopAction();
    this.initColumn();
  }

  initColumn() {
    this.tableConfig = {
      disableCardStyling: !this.isStandaloneUserTab,
      layout: TableLayout.MODERN,
      title: this.isStandaloneUserTab ? 'users' : undefined,
      description: this.isStandaloneUserTab ? 'manage user accounts, their access to the pef database and their license status.' : undefined,
      columnConfig: [
        {
          key: 'inline-action',
          cellStyle: { width: '50px' },
          cellDataType: TableDataType.DYNAMIC_COMPONENT,
          cellValue: ({ row }) => [
            InlineActionComponent,
            {
              row,
              layout: TableLayout.MODERN,
              dropdownIcon: 'dots-vertical',
              rightClickShowDropdown: true,
              style: { display: 'flex', justifyContent: 'center' },
              inlineActions: [
                {
                  icon: 'edit',
                  name: 'edit',
                  action: () => this.openModal(row)
                },
                {
                  icon: 'trash',
                  name: 'delete',
                  action: () => this.handleDeleteUserAdmin(row)
                }
              ]
            }
          ]
        },
        {
          title: 'name',
          key: 'name',
          sortable: true,
          searchable: true,
          filterable: FilterType.TEXT
        },
        {
          title: 'email',
          key: 'email',
          sortable: true,
          searchable: true,
          filterable: FilterType.TEXT
        },
        ...(!this.isAdminSystem ? [] :
          [{
            title: 'company',
            key: 'companyName',
            sortable: true,
            searchable: true,
            filterable: FilterType.TEXT
          }]),
        {
          title: 'access to the pef database',
          key: 'hasPefDatabaseAccess',
          sortable: true,
          searchable: true,
          filterable: FilterType.MULTI_SELECT,
          filterDataSource: [
            { id: true, name: 'enabled' },
            { id: false, name: 'disabled' }
          ],
          translateFilterOptions: true,
          cellDataType: TableDataType.DYNAMIC_COMPONENT,
          cellValue: ({ row }: any) => [ChipComponent, { type: row.hasPefDatabaseAccess ? 'enabled' : 'disabled' }]
        },
        {
          title: 'license status',
          key: 'licenseStatus',
          filterable: FilterType.MULTI_SELECT,
          filterDataSource: [
            { id: 'active', name: 'active' },
            { id: 'notLicensed', name: 'not licensed' }
          ],
          translateFilterOptions: true,
          cellDataType: TableDataType.DYNAMIC_COMPONENT,
          cellValue: ({ row }: any) => [LicenseStatusComponent, {
            status: row.licenseStatus,
            showCancelled: !!row.licenseCancelled
          }]
        },
        ...(!this.companyId ? [{
          title: 'active',
          key: 'isActive',
          sortable: true,
          searchable: true,
          filterable: FilterType.MULTI_SELECT,
          filterDataSource: [
            { id: true, name: 'active' },
            { id: false, name: 'inactive' }
          ],
          translateFilterOptions: true,
          cellDataType: TableDataType.DYNAMIC_COMPONENT,
          cellValue: ({ row }: any) => row.isActive == null ? [null, {}] : [ChipComponent, { type: row.isActive ? 'active' : 'inactive' }]
        }] : []),
        {
          title: 'last login',
          key: 'lastLogin',
          sortable: true,
          searchable: true,
          cellDataType: TableDataType.DYNAMIC_COMPONENT,
          cellValue: (cell: any) => [TextFormatterComponent, { cell, format: TextFormat.DATE_TIME_ZONE }]
        },
      ]
    }
  }

  initTopAction() {
    this.topOptions = {
      showSearch: true,
      inlineActions: true,
      topActions: [
        {
          name: 'add user',
          action: () => {
            this.openModal();
          },
          hidden: () => this.router.url.startsWith(ROUTES.ADMIN.USER),
        }
      ]
    }
  }

  fetchUsers = (params: any) => {
    const companyFilter = this.companyId ? `companyId[${PARAM_OPERATOR.EQUAL}]${this.companyId}` : '';
    const filterText = [companyFilter, params.filterText].filter(Boolean).join('&');
    const query = this.companyId ? { ...params, filterText } : params;
    return this.userService.getUsers(query).pipe(
      tap((response: any) => this.totalChanged?.(response.totalRecord ?? response.length ?? 0))
    );
  }

  onClickRow = ({ row }: any) => {
    this.openModal(row)
  }

  openModal(data?: any) {
    if (this.router.url.startsWith(ROUTES.ADMIN.COMPANIES)) {
      this.tableService.markForRestore(this.tableId);
      this.router.navigate([`${ROUTES.ADMIN.COMPANIES}/${this.companyId}/users`, data?.id ?? 'add']);
      return;
    }
    if (this.isAdminSystem && data?.id) {
      this.tableService.markForRestore(this.tableId);
      this.router.navigate([ROUTES.ADMIN.USER, data.id]);
      return;
    }
    const dialogRef = this.dialog.open(UserModalComponent, {
      width: DIALOG_WIDTH.MEDIUM,
      maxWidth: "90vw",
      disableClose: true,
      data: {userData: data, companyId: this.companyId}
    });
    dialogRef.afterClosed().subscribe((result?: ISubmission) => {
      result && this.dataTable.retrieveData();
    });
  }

  async handleDeleteUserAdmin(data: any) {
    this.toastService.confirmDelete({
      title: 'delete user',
      content: 'do you want to delete this',
      width: DIALOG_WIDTH.SMALLEST,
      onConfirm: () => {
        this.loadingService.show();
        this.userService.deleteUser(data.id).subscribe({
          next: () => {
            this.toastService.success(this.translateService.instant('delete successfully'));
            this.dataTable.retrieveData();
            this.loadingService.hide();
          },
          error: ({ error }) => {
            this.loadingService.hide();
            this.toastService.error(this.translateService.instant(error.Message));
          }
        });
      }
    });
  }
}
